export const SETTINGS_BASE_PATH = '/app/overview/settings';

const SECTION_SLUGS = {
  account: '',
  billing: 'billing',
  'my-services': 'my-services',
  booking: 'booking',
  business: 'business',
  hours: 'business-hours',
  locations: 'locations',
  'services-categories': 'services',
  'client-experience': 'client-experience',
  portal: 'client-experience',
  payments: 'payments',
  deluxe: 'deluxe',
  authorize: 'authorize',
  'gateway-environments': 'gateway-environments',
  notifications: 'notifications',
  'landing-page': 'landing-page',
};

const SLUG_TO_SECTION = {
  '': 'account',
  account: 'account',
  billing: 'billing',
  'my-services': 'my-services',
  booking: 'booking',
  business: 'business',
  'business-hours': 'hours',
  hours: 'hours',
  locations: 'locations',
  services: 'services-categories',
  'services-categories': 'services-categories',
  'client-experience': 'client-experience',
  portal: 'client-experience',
  payments: 'payments',
  deluxe: 'deluxe',
  authorize: 'authorize',
  'gateway-environments': 'gateway-environments',
  notifications: 'notifications',
  'landing-page': 'landing-page',
};

const SECTION_PARENT = {
  deluxe: 'payments',
  authorize: 'payments',
  'gateway-environments': 'payments',
  deposits: 'payments',
  tips: 'payments',
  portal: 'client-experience',
  'landing-page': 'client-experience',
};

const LEGACY_ACCOUNT_SECTIONS = new Set(['privacy', 'appearance', 'theme']);
const SERVICES_SECTIONS = new Set(['services', 'categories', 'client_categories', 'services-categories']);
const SERVICES_PARENTS = new Set(['services', 'categories', 'services-categories']);
const KNOWN_SLUGS = Object.keys(SLUG_TO_SECTION).filter(Boolean);

export const getSettingsSectionParent = (section) => SECTION_PARENT[section] || section;

export const normalizeSettingsSection = (section) => {
  if (!section || LEGACY_ACCOUNT_SECTIONS.has(section)) {
    return 'account';
  }
  if (section === 'email' || section === 'sms') {
    return 'notifications';
  }
  if (SERVICES_SECTIONS.has(section) || section === 'services-categories') {
    return 'services-categories';
  }
  if (section === 'portal') {
    return 'client-experience';
  }
  if (section === 'deposits' || section === 'tips') {
    return 'payments';
  }
  return SECTION_SLUGS[section] != null ? section : 'account';
};

const getSearchParams = (search = '') => (
  new URLSearchParams(search.startsWith('?') ? search : search ? `?${search}` : '')
);

const getSlugFromSearch = (search = '') => {
  const params = getSearchParams(search);
  const sectionValue = params.get('section');
  if (sectionValue && SLUG_TO_SECTION[sectionValue]) {
    return sectionValue;
  }

  return KNOWN_SLUGS.find((slug) => params.has(slug)) || '';
};

const getLegacyPathSlug = (pathname = '') => {
  if (!pathname.startsWith(SETTINGS_BASE_PATH)) {
    return '';
  }

  const rest = pathname.slice(SETTINGS_BASE_PATH.length).replace(/^\/+|\/+$/g, '');
  if (!rest || rest.startsWith('locations/')) {
    return '';
  }

  const slug = rest.split('/')[0] || '';
  return SLUG_TO_SECTION[slug] ? slug : '';
};

export const buildSettingsSectionPath = (section, { tab } = {}) => {
  const normalized = normalizeSettingsSection(section);
  const slug = SECTION_SLUGS[normalized];

  if (!slug) {
    return SETTINGS_BASE_PATH;
  }

  if (normalized === 'notifications' && (tab === 'sms' || tab === 'email')) {
    return `${SETTINGS_BASE_PATH}?${slug}=${tab}`;
  }

  return `${SETTINGS_BASE_PATH}?${slug}`;
};

export const parseSettingsSectionPath = (pathname = '', search = '') => {
  if (pathname.includes('/settings/locations/')) {
    return { section: 'locations', parent: 'locations' };
  }

  if (!pathname.startsWith(SETTINGS_BASE_PATH)) {
    return { section: 'account', parent: 'account' };
  }

  const slug = getSlugFromSearch(search) || getLegacyPathSlug(pathname);
  const section = SLUG_TO_SECTION[slug] || 'account';
  const params = getSearchParams(search);
  const slugValue = slug ? params.get(slug) : '';
  const tab = slugValue === 'sms' || slugValue === 'email'
    ? slugValue
    : params.get('tab');

  return {
    section,
    parent: getSettingsSectionParent(section),
    notificationsTab: tab === 'sms' || tab === 'email' ? tab : undefined,
    usesLegacyPath: Boolean(getLegacyPathSlug(pathname)),
  };
};

export const isSettingsSectionHome = (pathname = '', search = '') => {
  const parsed = parseSettingsSectionPath(pathname, search);
  return parsed.section === 'account' && !pathname.includes('/settings/locations/');
};

export const normalizeSettingsNavState = (state = {}) => {
  let section = state.section;
  let parent = state.parent;
  const { messagingTab, servicesTab } = state;

  if (LEGACY_ACCOUNT_SECTIONS.has(section)) {
    section = 'account';
  }
  if (LEGACY_ACCOUNT_SECTIONS.has(parent)) {
    parent = 'account';
  }

  if (parent === 'notifications' && (section === 'email' || section === 'sms')) {
    return {
      section: 'notifications',
      parent: 'notifications',
      notificationsTab: section,
    };
  }

  if (section === 'notifications' && (messagingTab === 'sms' || messagingTab === 'email')) {
    return {
      section: 'notifications',
      parent: 'notifications',
      notificationsTab: messagingTab,
    };
  }

  if (
    SERVICES_PARENTS.has(parent) ||
    SERVICES_SECTIONS.has(section) ||
    (section === 'services-categories' && (servicesTab === 'services' || servicesTab === 'categories'))
  ) {
    return {
      section: 'services-categories',
      parent: 'services-categories',
    };
  }

  const normalized = normalizeSettingsSection(section);
  return {
    section: normalized,
    parent: getSettingsSectionParent(normalized),
    notificationsTab: undefined,
  };
};

export const getCanonicalSettingsHref = (pathname = '', search = '') => {
  if (pathname.includes('/settings/locations/')) {
    return pathname;
  }

  const parsed = parseSettingsSectionPath(pathname, search);
  return buildSettingsSectionPath(parsed.section, { tab: parsed.notificationsTab });
};

export const getSettingsPathFromLocation = (location) => {
  if (location?.state?.section) {
    const normalized = normalizeSettingsNavState(location.state);
    return {
      ...normalized,
      path: buildSettingsSectionPath(normalized.section, { tab: normalized.notificationsTab }),
      fromState: true,
    };
  }

  const parsed = parseSettingsSectionPath(location?.pathname || '', location?.search || '');
  return {
    ...parsed,
    path: buildSettingsSectionPath(parsed.section, { tab: parsed.notificationsTab }),
    fromState: false,
  };
};
